import type {ReactElement} from 'react';
import {useState, useRef, useEffect} from 'react';
import styles from './ai-chat-inspiration.module.scss';
import {AiChatInspirationVisual} from './components/ai-chat-inspiration-visual';
import type {PillContent} from '@Components/pill-legacy/pill';
import {Pill, PILL_CONTENT_TYPE, PILL_THEME, PILL_TYPE} from '@Components/pill-legacy/pill';
import {Text, TextSize} from '@Components/text/text';
import {Icon} from '@Components/icon-v2';
import type {InspirationLayoutVariant, InspirationWidthVariant} from '@/ai-chat/components/ai-chat-window-inspirations/ai-chat-window-inspirations.types';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {ClickableDiv} from '@Components/clickable-div';

export interface AiChatInspirationProps {
  visualLayoutVariant: InspirationLayoutVariant;
  imageURL: string;
  imageURLToConvertTo?: string;
  attachmentURL?: string;
  pillText: string;
  pillIcon: string;
  prompt: string;
  widthVariant: InspirationWidthVariant;
  onRecreate: () => void;
  imageClasses?: string;
}

export function AiChatInspiration({
  visualLayoutVariant,
  imageURL,
  imageURLToConvertTo,
  attachmentURL,
  pillText,
  pillIcon,
  prompt,
  widthVariant,
  onRecreate,
  imageClasses,
}: AiChatInspirationProps): ReactElement {
  const [isHovered, setIsHovered] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const isSmall = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.LARGE);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return (): void => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const isLongPrompt = prompt.length >= 230;
  const isVeryLongPrompt = prompt.length > 300;

  const getPillContent = (): PillContent => {
    return {
      type: PILL_CONTENT_TYPE.HYBRID,
      text: pillText,
      icon: pillIcon,
      iconColor: 'content-secondary',
      gap: 4,
      textSize: TextSize.XXSMALL,
      isBold: true,
    };
  };

  const getCardClassName = (): string => {
    const widthClass = styles[`width${widthVariant}`] || '';
    const smallClass = isSmall ? styles.isSmall : '';
    const hoveredClass = isHovered ? styles.isHovered : '';
    const expandedClass = isExpanded ? styles.isExpanded : '';
    const longPromptClass = isLongPrompt ? styles.isLongPrompt : '';
    const veryLongPromptClass = isVeryLongPrompt ? styles.isVeryLongPrompt : '';
    return `${styles.inspirationCard} ${widthClass} ${smallClass} ${hoveredClass} ${expandedClass} ${longPromptClass} ${veryLongPromptClass}`;
  };

  const getOverlayClassName = (): string => {
    return `${styles.overlay} ${isOverlayVisible ? styles.overlayActive : ''}`;
  };

  const handleClick = (): void => {
    if (isSmall) {
      setIsExpanded(!isExpanded);
    } else {
      onRecreate();
    }
  };

  const handleAnimationStart = (): void => {
    setIsOverlayVisible(false);
  };

  const handleMouseEnter = (): void => {
    if (!isSmall) {
      setIsOverlayVisible(true);
      setIsExpanded(true);

      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(true);
      }, 500);
    }
  };

  const handleMouseLeave = (): void => {
    if (!isSmall) {
      setIsOverlayVisible(false);
      setIsHovered(false);
      setIsExpanded(false);

      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    }
  };

  const renderPill = (): ReactElement => {
    return <Pill content={getPillContent()} type={PILL_TYPE.RECTANGLE} theme={PILL_THEME.SECONDARY} className={styles.pill} />;
  };

  const renderPromptText = (size?: TextSize): ReactElement => {
    return <Text className={`${styles.prompt} ${isExpanded ? styles.expandedPrompt : ''} ${isSmall ? styles.isSmallScreenPrompt : ''}`} val={prompt} size={size} />;
  };

  const renderRecreateButton = (): ReactElement => {
    return (
      <Button
        type={ButtonType.SECONDARY}
        size={ButtonSize.LARGE}
        text={window.i18next.t('pmwjs_ai_chat_recreate_this')}
        icon="icon-sparkle-filled"
        onClick={onRecreate}
        isFullWidth={true}
        customClasses={styles.recreateButton}
      />
    );
  };

  const renderOverlayText = (): ReactElement => {
    return (
      <>
        <Text val={window.i18next.t('pmwjs_ai_chat_recreate_this')} size={TextSize.SMALL} bold className="content-body-white" />
        <Icon icon="icon-sparkle-filled" size={IconSize.SIZE_ICON_20} className="content-body-white" />
      </>
    );
  };

  const renderMobileView = (): ReactElement => {
    return (
      <>
        <div className={styles.mobileContainer}>
          <div className={styles.mobileCardInfo}>
            <div className={styles.mobileVisual}>
              <AiChatInspirationVisual
                imageURL={imageURL}
                imageURLToConvertTo={imageURLToConvertTo}
                attachmentURL={attachmentURL}
                layoutVariant={visualLayoutVariant}
                isHovered={false}
                isMobileView={true}
                imageClasses={imageClasses}
                isExpanded={isExpanded}
              />
            </div>
            <div className={styles.mobileContent}>
              {renderPill()}
              {renderPromptText(TextSize.XSMALL)}
            </div>
          </div>
          {getMobileRecreateButtonContainer()}
        </div>
      </>
    );
  };

  const getMobileRecreateButtonContainer = (): ReactElement | null => {
    if (!isExpanded) {
      return null;
    }

    return <div className={styles.mobileRecreateContainer}>{renderRecreateButton()}</div>;
  };

  const renderDesktopView = (): ReactElement => {
    return (
      <>
        <div className={styles.visualWrapper}>
          <AiChatInspirationVisual
            imageURL={imageURL}
            imageURLToConvertTo={imageURLToConvertTo}
            attachmentURL={attachmentURL}
            layoutVariant={visualLayoutVariant}
            isHovered={isHovered}
            imageClasses={imageClasses}
            isExpanded={isExpanded}
            onAnimationStart={handleAnimationStart}
          />
          <div className={getOverlayClassName()}>{renderOverlayText()}</div>
        </div>
        <div className={styles.cardInfo}>
          {renderPill()}
          {renderPromptText(TextSize.XSMALL)}
        </div>
      </>
    );
  };

  return (
    <ClickableDiv className={getCardClassName()} onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {isSmall ? renderMobileView() : renderDesktopView()}
    </ClickableDiv>
  );
}
